<template>
  <LISANumber
    v-if="isLISA3"
    v-bind="$attrs"
    :model-value="props.modelValue ?? undefined"
    :locale="inputLocale"
    :precision="maxFractionDigits ?? 10"
    :min="props.min ?? undefined"
    :max="props.max ?? undefined"
    :step="props.step ?? undefined"
    :disabled="props.disabled"
    :readonly="props.readonly"
    :invalid="props.invalid"
    :use-grouping="props.useGrouping"
    :tooltip="tooltip"
  >
    <template v-if="'append' in $slots" #append>
      <slot name="append" />
    </template>
  </LISANumber>
  <InputNumber
    v-else
    v-bind="{ ...$attrs, ...$props }"
    :locale="inputLocale"
    :input-class="inputClassComputed"
    :max-fraction-digits="maxFractionDigits ?? 2"
  />
</template>

<script setup lang="ts">
// eslint-disable-next-line no-restricted-imports
import InputNumber, { type InputNumberProps } from "primevue/inputnumber";
import { computed } from "vue";
import { useTranslation } from "@/language/translation";
import { LISANumber } from "@fi-solutions/lisa-ui";
import { isLISA3 } from "@/composables/useLISA3";

/**
 * defaults are set because of vue bug with boolean collapsing to false
 * https://github.com/vuejs/core/issues/8576
 */
const props = withDefaults(
  defineProps<
    Omit<InputNumberProps, "locale"> & {
      rightAlign?: boolean;
      tooltip?: string;
    }
  >(),
  {
    inputClass: undefined,
    format: undefined,
    useGrouping: undefined,
    allowEmpty: undefined,
    tooltip: undefined,
  },
);

const { locale } = useTranslation();
const inputLocale = computed(() => {
  const availableLocales = [
    "zh-CN",
    "da-DK",
    "en-GB",
    "en-US",
    "fr-FR",
    "de-DE",
    "it-IT",
    "ja-JP",
    "ko-KR",
    "pl-PL",
    "ru-RU",
    "es-ES",
    "sv-SE",
  ];
  if (locale.value === "en_GB") {
    return "en-GB";
  }
  if (locale.value === "en") {
    return "en-US";
  }
  return availableLocales.find((el) => el.startsWith(locale.value.split("-")[0])) || "en-EN";
});

const inputClassComputed = computed(() => {
  let result = props.rightAlign === true ? "text-right" : undefined;

  if (props.inputClass) {
    if (typeof props.inputClass === "string") {
      result = (result ?? "") + ` ${props.inputClass}`;
    } else if (result == null) {
      return props.inputClass;
    } else {
      return {
        [result]: !!props.rightAlign,
        ...props.inputClass,
      };
    }
  }
  return result;
});
</script>

<style lang="scss" scoped></style>
